import {Form, Tooltip} from 'antd';
import cn from 'classnames';
import React, {FC, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';

import {DateInput} from 'components/@common/inputs/date-input';
import {Search} from 'components/@common/inputs/search';
import {CustomTable} from 'components/@common/widgets/custom-table';
import {CollapsibleHeader} from 'components/collapsible-header/collapsible-header';
import {DynamicIcon} from 'components/dynamic-icon';
import {FilterForm} from 'components/filter-form/filter-form';
import {CustomSelect, ICustomSelectEntry} from 'components/form/inputs/custom-select';
import {Spinner} from 'components/spinner';
import {
    ColumnsSettingButton,
} from 'components/table-report/components/table-report-actions/actions/columns-setting-button/columns-setting-button';
import {
    RefreshButton,
} from 'components/table-report/components/table-report-actions/actions/refresh-button/refresh-button';
import {createStringDate} from 'components/table/utils/table.utils';
import {UIBlocker} from 'components/ui-blocker';
import {DEFAULT_PAGE_SIZE} from 'features/calculate-control-ratios/model/constants';
import {selectContextRawData} from 'modules/context/context-selector';
import {SUCCESSFULLY_UPLOADED} from 'pages/external-loading/etl-audit-page';
import {DATE_WITH_TIME_DOTS} from 'shared/constants/date-format';
import {URL_OPEN_INTERFACE} from 'shared/constants/urls';
import {usePagination} from 'shared/hooks/use-pagination';
import {ILookupEntry} from 'shared/types/lookups';
import {getTablePaginationDefaultConfig, showMessage} from 'shared/utils';
import {useAppSelector} from 'store/config/hooks';
import {selectMicroserviceToken} from 'store/slices/auth-slice/auth-slice-selectors';
import {resetEtlAudit, selectEtlAuditData} from 'store/slices/etl-slice';
import {fetchEtlUploadScenario} from 'store/slices/etl-slice/api/api';
import {loadEtlAudit} from 'store/slices/etl-slice/api/thunks';
import {selectEtlAuditDataLoading} from 'store/slices/etl-slice/model/selectors';

import {
    AuditDownloadManagerModal,
} from './components/audit-download-manager-modal';
import s from './etl-audit-page.module.less';

interface IPaginationType {
    page?: number;
    size?: number;
}

export const EtlAuditPage: FC = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const etlData = useAppSelector(selectEtlAuditData);
    const isLoading = useAppSelector(selectEtlAuditDataLoading);
    const {organizationId} = useAppSelector(selectContextRawData) ?? {};

    const [filtersData, setFiltersData] = useState<Record<string, any> | undefined>();
    const [search, setSearch] = useState<string | null>(null);
    const [areFiltersDisabled, setAreFiltersDisabled] = useState(false);
    const [pagination, setPagination] = useState<IPaginationType>(
        {page: 1, size: DEFAULT_PAGE_SIZE},
    );
    const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
    const [uploadScenarioEntries, setUploadScenarioEntries] = useState<ICustomSelectEntry<string, string>[]>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadId, setLoadId] = useState<number>();
    const token = useAppSelector(selectMicroserviceToken);

    useEffect(() => {
        if (token) {
            (async () => {
                try {
                    const referenceData = await fetchEtlUploadScenario();
                    const transformedRefData = referenceData.data.map((refData: ILookupEntry) => ({
                        value: refData.meaning,
                        label: refData.description,
                    }));
                    setUploadScenarioEntries(transformedRefData);
                } catch (e) {
                    showMessage({message: e.response.statusText, isError: true});
                }
            })();
        }
    }, [token]);

    const updateData = () => {
        if (filtersData?.actionBegDttm && filtersData?.actionEndDttm) {
            dispatch(loadEtlAudit(
                {
                    ...filtersData,
                    search: search || '',
                    paginationCurrent: pagination.page,
                    paginationPageSize: pagination.size,
                    orgId: organizationId,
                },
            ));
        }
    };

    useEffect(() => {
        if (token) {
            updateData();
        }
    }, [filtersData, search, pagination, organizationId]);

    const handleSubmitFilters = (filters?: Record<string, any>) => {
        if (filters) setFiltersData(filters);
        setAreFiltersDisabled(true);
    };

    const handleEditFilters = () => {
        setAreFiltersDisabled(false);
    };

    const handleResetFilters = () => {
        setFiltersData(undefined);
    };

    const handleModalOrRedirectClick = (id: number, scenarioId: number, srcId: number, shouldOpenModal: boolean) => {
        if (shouldOpenModal) {
            setLoadId(id);
            setIsModalOpen(true);
            return;
        }
        history.push(`${URL_OPEN_INTERFACE}?loadId=${id}&scenarioId=${scenarioId}&srcId=${srcId}`);
    };

    useEffect(() => () => {
            dispatch(resetEtlAudit());
        }
    , []);

    const columns = [
        {
            title: '',
            dataIndex: 'stat',
            key: 'stat',
            render: (value: string, record: any) => {
                let tooltipTitle = '';
                let shouldOpenModal = false;
                let isDisabled = true;
                if (record.canNavigateToOpenInterface && record?.statusCode === SUCCESSFULLY_UPLOADED) {
                    tooltipTitle = 'Перейти в "Открытый интерфейс"';
                    isDisabled = false;
                }
                if (record.isHistoryModalActive) {
                    tooltipTitle = 'Открыть историю операций';
                    isDisabled = false;
                    shouldOpenModal= true;
                }

                return (
                    <Tooltip
                        mouseEnterDelay={0.7}
                        placement="right"
                        title={tooltipTitle}
                    >
                        <DynamicIcon
                            className={cn(s.icon, isDisabled && s['icon-disabled'])}
                            type="OpenInterface"
                            onClick={() => handleModalOrRedirectClick(
                                record.loadId, record.scenarioId, record.srcId, shouldOpenModal,
                            )}
                        />
                    </Tooltip>
                );
            },
        },
        {
            title: 'ID загрузки',
            dataIndex: 'loadId',
            key: 'loadId',
        },
        {
            title: 'ID пакета',
            dataIndex: 'pkgId',
            key: 'pkgId',
        },
        {
            title: 'Действие ',
            dataIndex: 'actionDescription',
            key: 'actionDescription',
        },
        {
            title: 'Статус ',
            dataIndex: 'statusDescription',
            key: 'statusDescription',
        },
        {
            title: 'Дата и время начала',
            dataIndex: 'actionBegDttm',
            key: 'actionBegDttm',
            render: (value: string) => (
                <span>{createStringDate(value, DATE_WITH_TIME_DOTS)}</span>
            ),
        },
        {
            title: 'Дата и время окончания',
            dataIndex: 'actionEndDttm',
            key: 'actionEndDttm',
            render: (value: string) => (
                <span>{createStringDate(value, DATE_WITH_TIME_DOTS)}</span>
            ),
        },
        {
            title: 'Количество строк',
            dataIndex: 'quantityLines',
            key: 'quantityLines',
        },
        {
            title: 'Инициатор',
            dataIndex: 'userName',
            key: 'userName',
        },
    ];

    const hideColumns = (cols: string[]) => {
        setHiddenColumns(cols);
    };

    const header = (
        <CollapsibleHeader header="Аудит ETL-процессов">
            <FilterForm
                handleSubmit={handleSubmitFilters}
                handleEdit={handleEditFilters}
                handleReset={handleResetFilters}
                title="Параметры отчета"
            >
                <Form.Item
                    key="actionBegDttm"
                    name="actionBegDttm"
                    label="Начало периода"
                    required
                    rules={[
                        {
                            required: true,
                            message: 'Поле должно быть заполнено',
                        },
                    ]}
                >
                    <DateInput
                        resetTimeOnSet
                        disabled={areFiltersDisabled}
                    />
                </Form.Item>
                <Form.Item
                    key="actionEndDttm"
                    name="actionEndDttm"
                    label="Конец периода"
                    required
                    rules={[
                        {
                            required: true,
                            message: 'Поле должно быть заполнено',
                        },
                    ]}
                >
                    <DateInput
                        resetTimeOnSet
                        disabled={areFiltersDisabled}
                    />
                </Form.Item>
                <Form.Item
                    key="scenarioCode"
                    name="scenarioCode"
                    label="Сценарий загрузки"
                >
                    <CustomSelect
                        style={{minWidth: 600}}
                        settings={{
                            showSearch: true,
                            isClearable: true,
                            placeholder: 'Сценарий загрузки',
                            isDisabled: areFiltersDisabled,
                        }}
                        entries={uploadScenarioEntries}
                        fixedDropdown={false}
                    />
                </Form.Item>
            </FilterForm>
        </CollapsibleHeader>
    );

    const {
        pageSize,
        handleChange,
    } = usePagination({
        pageDefault: 1,
        pageSizeDefault: DEFAULT_PAGE_SIZE,
        onChange: (page, size) => {
            setPagination({page, size});
        },
    });

    const actions = (
        <div className={s['actions-wrapper']}>
            <div className={s.actions}>
                <ColumnsSettingButton
                    hideColumns={hideColumns}
                    hiddenColumnsKeys={hiddenColumns}
                    initialColumns={columns}
                />
                <RefreshButton
                    handleRefreshReportPage={updateData}
                />
            </div>
            <Search
                setSearch={setSearch}
                fetchCallback={() => {}}
            />
        </div>
    );

    return (
        <div className={s.wrapper}>
            <UIBlocker
                content={isLoading ? (
                    <Spinner />
                ) : undefined}
            >
                {header}
                {actions}
                <CustomTable
                    className={s.table}
                    dataSource={etlData?.dataTable}
                    columns={columns.filter(column => !hiddenColumns.includes(column?.dataIndex))}
                    pagination={{
                        ...getTablePaginationDefaultConfig({defaultPageSize: DEFAULT_PAGE_SIZE}),
                        total: etlData?.recordsTotal,
                        pageSize,
                        onChange: handleChange,
                    }}
                />
                <AuditDownloadManagerModal
                    loadId={loadId}
                    isOpen={isModalOpen}
                    setOpen={setIsModalOpen}
                />
            </UIBlocker>
        </div>
    );
};
